import { AccordionDetails, CSSObject, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import { QueryStatus } from '@reduxjs/toolkit/query';
// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult, FuseResultMatch } from 'fuse.js';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  calizaApi as api,
  useCreateTransactionMutation,
  useGetBeneficiariesQuery,
  useGetBitsoBanksByCountryCodeQuery,
  useGetConfigurationQuery,
  useGetContactsQuery,
  useGetDashboardOperationsQuery,
} from '@/api/v1';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { HighlightedText } from '@/components/atoms/HighlightedText';
import { AddObjectButton } from '@/components/molecules/AddObjectButton';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { FundingMethodSelect } from '@/components/molecules/FundingMethodSelect';
import { FxMarkupBanner } from '@/components/molecules/FxMarkupBanner';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { RecipientsSelect } from '@/components/molecules/RecipientsSelect';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { Section } from '@/components/molecules/Section';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { FxMarkupModal } from '@/components/organisms/FxMarkupModal';
import { Header } from '@/components/organisms/Header';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import { useBalances } from '@/hooks/useBalances';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useCustomer } from '@/hooks/useCustomer';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { DocumentSections } from '@/Screens/Dashboard/Flows/TransferFlow/DocumentSections';
import {
  beneficiariesLoaded,
  beneficiaryChanged,
  fromAmountChanged,
  fundingMethodChanged,
  initialState,
  mainBeneficiarySet,
  markupBpsChanged,
  paymentReferenceChanged,
  recipientAccountChanged,
  sameDayAchChanged,
  senderTypeChanged,
  simulationCreated,
  toAmountChanged,
  transactionFlowChanged,
  TransferFlowAction,
  transferFlowReducer,
  TransferFlowState,
  transferPurposeChanged,
} from '@/Screens/Dashboard/Flows/TransferFlow/reducer';
import { Amount } from '@/types/amount';
import { DetailedBalance } from '@/types/bankInfo';
import { Beneficiary } from '@/types/beneficiary';
import { BitsoBank } from '@/types/bitsoBank';
import { Contact, RecipientAccount } from '@/types/contacts';
import { CountryType } from '@/types/country';
import { isCountryType } from '@/types/country.ext';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  FundingMethodItem,
  PaymentRailType,
} from '@/types/currency';
import { Locale } from '@/types/locale';
import { NotNullable } from '@/types/notNullable';
import { RecipientResponse } from '@/types/recipient';
import { CustomFees, NfTransactionDetailsResponse } from '@/types/simulation';
import { MarketExecutionType, TransactionResponse } from '@/types/transaction';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import { checkStringValidSwiftCharacters } from '@/utility/checkStringValidSwiftCharacters';
import { toValidCurrency } from '@/utility/currencies';
import { isFetchError } from '@/utility/reactToolkit';
import { getRecipientCurrency } from '@/utility/recipientDisplayAdapter';
import { AsAnyActionArg } from '@/utility/reducers';

enum FormErrors {
  MISSING_ACCOUNT = 'MISSING_ACCOUNT',
  MISSING_FUNDING_METHOD = 'MISSING_FUNDING_METHOD',
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  PAYMENT_REFERENCE_INVALID_CHARACTERS = 'PAYMENT_REFERENCE_INVALID_CHARACTERS',
  MISSING_SUPPORTING_DOCUMENTATION = 'MISSING_SUPPORTING_DOCUMENTATION',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

interface FilteredBeneficiary {
  readonly item: Beneficiary;
  readonly matchIndices?: ReadonlyArray<readonly [number, number]>;
}

const BENEFICIARY_BALANCE_CURRENCIES = [
  CurrencyCodeType.USD,
  CurrencyCodeType.USDC,
  CurrencyCodeType.USDT,
];
const CUSTOMER_BALANCE_CURRENCIES = [
  CurrencyCodeType.USD,
  CurrencyCodeType.USDC,
  CurrencyCodeType.USDT,
] as const;

export const TransferFlow: React.FC = (): React.ReactElement => {
  const reducer = React.useReducer<TransferFlowState, AsAnyActionArg<TransferFlowAction>>(
    transferFlowReducer,
    initialState,
  );
  const [state, dispatch] = reducer;

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const tr = useTranslator();
  const globalDispatch = useDispatch();
  const formatter = useCurrencyFormatter();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const locale = React.useContext<Locale>(LocaleContext);

  const [error, setError] = React.useState<FormErrors | null>(null);
  const [customerSearchKeyword, setCustomerSearchKeyword] = React.useState<string>('');
  const [isFxMarkupModalOpen, setIsFxMarkupModalOpen] = React.useState(false);
  const [appliedMarkupBps, setAppliedMarkupBps] = React.useState<number | null>(null);
  const isFxMarkupEnabled = useIntegratorFeatureFlag('dashboard-fx-markup');

  const customer = useCustomer();
  const { data: fetchedBeneficiaries = [] } = useGetBeneficiariesQuery();
  const { data: combinedContacts = [], isFetching: loadingContacts } = useGetContactsQuery(
    customer.beneficiaryId,
  );
  const { data: operations = [] } = useGetDashboardOperationsQuery('TRANSFER');
  const { data: colombianBanks = [] } = useGetBitsoBanksByCountryCodeQuery(CountryType.CO);
  const {
    selectedOperation,
    fundingMethod,
    fromAmount,
    toAmount,
    recipientAccountId,
    paymentReference,
    transferPurpose,
    selectedBeneficiaryId,
    beneficiaries,
    mainBeneficiaryId,
    senderType,
    sameDayAch,
    markupBps,
  } = state;

  const beneficiaryBalances = useBalances({
    currencies: BENEFICIARY_BALANCE_CURRENCIES,
  });

  const customerBalances = useBalances({
    beneficiaryId: senderType === 'customer' ? selectedBeneficiaryId : undefined,
    currencies: CUSTOMER_BALANCE_CURRENCIES,
  });

  const [createTransaction, mutation] = useCreateTransactionMutation();

  const balances = React.useMemo(
    (): Partial<Record<CurrencyCodeType, DetailedBalance>> => ({
      [CurrencyCodeType.USD]: beneficiaryBalances.USD,
      [CurrencyCodeType.USDC]: beneficiaryBalances.USDC,
      [CurrencyCodeType.USDT]: beneficiaryBalances.USDT,
    }),
    [beneficiaryBalances.USD, beneficiaryBalances.USDC, beneficiaryBalances.USDT],
  );

  const recentlyAddedAccountId = React.useMemo((): string | null => {
    const { state } = location;
    return state?.accountId ?? null;
  }, [location]);

  const banksByCountry = React.useMemo(
    (): Partial<Record<CountryType, Record<string, string>>> => ({
      [CountryType.CO]: Object.fromEntries(
        colombianBanks.map((bank: BitsoBank): [string, string] => [bank.code, bank.name]),
      ),
    }),
    [colombianBanks],
  );

  const contacts = React.useMemo(
    (): readonly Contact[] =>
      combinedContacts.filter((contact: Contact): boolean => !contact.belongsToBeneficiary),
    [combinedContacts],
  );

  const destinationCountry = React.useMemo((): CountryType => {
    const allRecipients =
      contacts
        .map((contact: Contact): readonly RecipientResponse[] => {
          return contact.recipients;
        }, [])
        ?.flat() ?? [];
    const selectedRecipient = allRecipients.find(
      (recipient: RecipientResponse): boolean => recipient.id === recipientAccountId,
    );

    if (!selectedRecipient) {
      return CountryType.US;
    }

    switch (selectedRecipient.type) {
      case PaymentRailType.ACH:
        break;
      case PaymentRailType.Wire:
      case PaymentRailType.unset:
        return CountryType.US;
      case PaymentRailType.COP:
        return CountryType.CO;
      case PaymentRailType.SPEI:
        return CountryType.MX;
      case PaymentRailType.CVU:
        return CountryType.AR;
      case PaymentRailType.PIX:
        return CountryType.BR;
      case PaymentRailType.Swift:
        return getBankCountryFromRecipient(selectedRecipient) ?? CountryType.US;
      default:
        return CountryType.US;
    }

    return CountryType.US;
  }, [contacts, recipientAccountId]);

  const fundingMethods = React.useMemo((): readonly FundingMethodItem[] => {
    const currency = getToCurrencyFromAccount(contacts, recipientAccountId);
    const rail = getToRailFromAccount(contacts, recipientAccountId);
    const destinations = operations
      .map(
        (
          operation: DashboardOperation,
        ): readonly (DashboardOperationDestination & {
          readonly fromCurrency: CurrencyCodeType;
        })[] => {
          const { destinations, currency: fromCurrency } = operation;
          return destinations.map(
            (
              destination: DashboardOperationDestination,
            ): DashboardOperationDestination & { readonly fromCurrency: CurrencyCodeType } => ({
              ...destination,
              fromCurrency: fromCurrency,
            }),
          );
        },
      )
      .flat()
      .filter((destination: DashboardOperationDestination): boolean => {
        return destination.currency === currency && destination.rail === rail;
      });

    return destinations
      .map(
        (
          destination: DashboardOperationDestination & { readonly fromCurrency: CurrencyCodeType },
        ): readonly FundingMethodItem[] => {
          const { currency: toCurrency, fromCurrency, rail, fundingMethods } = destination;

          return fundingMethods.map(
            (fundingMethod: PaymentRailType): FundingMethodItem => ({
              id: [rail, fromCurrency, toCurrency, fundingMethod].join('.'),
              currency: fromCurrency,
              method: fundingMethod,
            }),
          );
        },
      )
      .flat();
  }, [contacts, operations, recipientAccountId]);

  const currentDestination = React.useMemo((): DashboardOperationDestination => {
    const currency = getToCurrencyFromAccount(contacts, recipientAccountId);
    const rail = getToRailFromAccount(contacts, recipientAccountId);
    const destinations = operations
      .map(
        (operation: DashboardOperation): readonly DashboardOperationDestination[] =>
          operation.destinations,
      )
      .flat()
      .filter((destination: DashboardOperationDestination): boolean => {
        return destination.rail === rail && destination.currency === currency;
      });

    if (destinations.length == 1) {
      return {
        currency: currency,
        rail: rail,
        fundingMethods: [fundingMethod.method],
      };
    }

    return {
      currency: currency,
      rail: rail,
      fundingMethods: [fundingMethod.method],
    };
  }, [contacts, fundingMethod.method, operations, recipientAccountId]);

  const currentBalance = React.useMemo((): DetailedBalance => {
    const effectiveBalances =
      fundingMethod?.fundingSource === 'customer' ? customerBalances : balances;
    return (
      effectiveBalances[fundingMethod.currency] ?? DetailedBalance.zero(fundingMethod.currency)
    );
  }, [balances, customerBalances, fundingMethod?.fundingSource, fundingMethod.currency]);

  const fromCurrency = React.useMemo(
    (): CurrencyCodeType => selectedOperation?.currency ?? CurrencyCodeType.UNSET,
    [selectedOperation?.currency],
  );

  const showFxMarkup =
    isFxMarkupEnabled &&
    senderType === 'customer' &&
    toValidCurrency(fromCurrency) !== toValidCurrency(currentDestination.currency);

  React.useEffect((): void => {
    if (toValidCurrency(fromCurrency) === toValidCurrency(currentDestination.currency)) {
      setAppliedMarkupBps(null);
      dispatch(markupBpsChanged(null));
    }
  }, [fromCurrency, currentDestination.currency, dispatch]);

  const [attachment, setAttachment] = React.useState<File | null>(null);

  const payorId = React.useMemo((): string | undefined => {
    if (fundingMethod?.fundingSource === 'customer') {
      return selectedBeneficiaryId || undefined;
    }
    return customer.beneficiaryId ?? undefined;
  }, [fundingMethod?.fundingSource, selectedBeneficiaryId, customer.beneficiaryId]);

  const customFees = React.useMemo((): CustomFees | undefined => {
    if (markupBps === null || markupBps === 0) {
      return undefined;
    }

    return { INTEGRATOR_SPREAD: markupBps };
  }, [markupBps]);

  const [simulation, fetchingSimulation] = useSimulation({
    fromAmount: fromAmount,
    toAmount: toAmount,
    simulationId: null,
    fromCurrency: fromCurrency,
    toCurrency: currentDestination.currency,
    fundingMethod: fundingMethod.method,
    paymentRail: currentDestination.rail,
    destination: recipientAccountId,
    beneficiaryId: selectedBeneficiaryId,
    payorId: payorId,
    customFees: customFees,
  });

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  const toCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return [currentDestination.currency];
  }, [currentDestination]);

  const fromCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return [];
  }, []);

  const isDocumentRequired = React.useMemo((): boolean => {
    return [CurrencyCodeType.USD, CurrencyCodeType.SGD, CurrencyCodeType.HKD].includes(
      currentDestination.currency,
    );
  }, [currentDestination.currency]);

  const accounts = React.useMemo(
    (): readonly RecipientAccount[] =>
      contacts.reduce(
        (accounts: readonly RecipientAccount[], contact: Contact): readonly RecipientAccount[] => {
          return [
            ...accounts,
            ...Contact.toRecipientAccounts(locale, banksByCountry, contact, displayUsdcAsUsd),
          ];
        },
        [],
      ) ?? [],
    [banksByCountry, contacts, displayUsdcAsUsd, locale],
  );

  const handleConfirm = React.useCallback((): void => {
    if (!recipientAccountId) {
      return setError(FormErrors.MISSING_ACCOUNT);
    }

    if (paymentReference && !checkStringValidSwiftCharacters(paymentReference)) {
      return setError(FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS);
    }

    if (!fundingMethod?.id) {
      return setError(FormErrors.MISSING_FUNDING_METHOD);
    }

    if (!fromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }

    const isWalletAndOverflowsBalance =
      fundingMethod.method === PaymentRailType.CalizaWallet &&
      currentBalance.available < fromAmount;
    if (isWalletAndOverflowsBalance) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }

    if (isDocumentRequired && transferPurpose !== 'eFxOperation' && !attachment) {
      return setError(FormErrors.MISSING_SUPPORTING_DOCUMENTATION);
    }

    if (simulation.id) {
      const basePayload = {
        beneficiaryIp: '',
        simulationId: simulation.id,
        marketExecutionType: MarketExecutionType.executeImmediately,
        paymentReference: paymentReference,
        transferPurpose: transferPurpose,
        efx: transferPurpose === 'eFxOperation',
        achSameDay: sameDayAch || undefined,
      };

      if (attachment) {
        createTransaction({
          ...basePayload,
          attachment: attachment,
        });
      } else {
        createTransaction(basePayload);
      }

      // FIXME: this is a workaround, it should be removed eventually
      setTimeout((): void => {
        globalDispatch(
          api.util.invalidateTags([
            {
              type: 'Balances',
              id: fromCurrency,
            },
          ]),
        );
      }, 2000);
    }
  }, [
    recipientAccountId,
    paymentReference,
    fundingMethod?.id,
    fundingMethod.method,
    fromAmount,
    currentBalance.available,
    isDocumentRequired,
    attachment,
    simulation.id,
    createTransaction,
    transferPurpose,
    sameDayAch,
    globalDispatch,
    fromCurrency,
  ]);

  const handleTransactionSuccess = React.useCallback(
    (response: TransactionResponse): void => {
      if (fundingMethod.method !== PaymentRailType.CalizaWallet) {
        navigate(`payment?transactionId=${response.id}`, {
          state: { fundingMethod: fundingMethod.method },
        });
      } else {
        navigate('completed');
      }
    },
    [fundingMethod, navigate],
  );

  React.useEffect((): void => {
    switch (mutation.status) {
      case QueryStatus.fulfilled:
        mutation.reset();
        handleTransactionSuccess(mutation.data);
        break;
      case QueryStatus.rejected:
        if (
          isFetchError(mutation.error) &&
          mutation.error.data.code === 'transaction.out_of_market_hours'
        ) {
          toast.show(tr('BRL_MARKET_HOURS_ALERT'), 'error');
        } else {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        }
        break;
      default:
    }
  }, [mutation, toast, handleTransactionSuccess, navigate, tr]);

  const setRecipientAccount = React.useCallback(
    (accountId: string): void => {
      if (error === FormErrors.MISSING_ACCOUNT) {
        setError(null);
      }

      dispatch(recipientAccountChanged(accountId));
    },
    [error, setError, dispatch],
  );

  const setToAmount = React.useCallback(
    (amount: number | null): void => {
      setError(resetAmountRelatedErrors);
      dispatch(toAmountChanged(amount));
    },
    [setError, dispatch],
  );

  const setFromAmount = React.useCallback(
    (amount: number | null): void => {
      setError(resetAmountRelatedErrors);
      dispatch(fromAmountChanged(amount));
    },
    [setError, dispatch],
  );

  const setFundingMethod = React.useCallback(
    (fundingMethod: FundingMethodItem): void => {
      const transactionFlow = operations.find(
        (operation: DashboardOperation): boolean =>
          operation.currency === fundingMethod.currency &&
          operation.destinations.some((dest: DashboardOperationDestination): boolean =>
            dest.fundingMethods.includes(fundingMethod.method),
          ),
      );

      if (error === FormErrors.MISSING_FUNDING_METHOD) {
        setError(null);
      }

      if (transactionFlow) {
        dispatch(transactionFlowChanged(transactionFlow));
        dispatch(fundingMethodChanged(fundingMethod));
      } else {
        console.debug(`unsupported funding method ${fundingMethod.id}`);
      }
    },
    [dispatch, operations, error, setError],
  );

  const creatingTransaction = React.useMemo(
    (): boolean => mutation.status === QueryStatus.pending,
    [mutation.status],
  );

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours) ||
      isBRLOperationOutsideMarketHours(
        currentDestination.currency,
        configuration.brazilianMarketHours,
      )
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
    currentDestination.currency,
  ]);

  const confirmDisabled = React.useMemo((): boolean => {
    if (!simulation?.id) {
      return true;
    }

    return (
      shouldNotAllowOperation ||
      mutation.status === QueryStatus.pending ||
      mutation.status === QueryStatus.fulfilled
    );
  }, [mutation.status, shouldNotAllowOperation, simulation?.id]);

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (error === FormErrors.MISSING_SUPPORTING_DOCUMENTATION) {
        setError(null);
      }

      const target = event.target as HTMLInputElement;
      const files = target.files ?? [];

      if (files[0]) {
        setAttachment(files[0]);
      }
    },
    [error, setError],
  );

  const handleFileRemove = React.useCallback((): void => {
    setAttachment(null);
  }, []);

  const handlePurposeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;
      dispatch(transferPurposeChanged(value));
      // Clear attachment when eFX operation is selected
      if (value === 'eFxOperation') {
        setAttachment(null);

        if (error === FormErrors.MISSING_SUPPORTING_DOCUMENTATION) {
          setError(null);
        }
      }
    },
    [dispatch, error, setError],
  );

  const handlePaymentReferenceChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;
      if (error === FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS) {
        setError(null);
      }
      dispatch(paymentReferenceChanged(value));
    },
    [error, setError, dispatch],
  );

  const handleBeneficiaryChange = React.useCallback(
    (event: SelectChangeEvent<string>): void => {
      setRecipientAccount('');
      dispatch(beneficiaryChanged(event.target.value));
      setCustomerSearchKeyword('');
    },
    [dispatch, setRecipientAccount],
  );

  const handleSenderTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newSenderType = event.target.value as 'myself' | 'customer';
      dispatch(senderTypeChanged(newSenderType));

      // If "Myself" is selected, automatically set the main beneficiary
      if (newSenderType === 'myself' && customer.beneficiaryId) {
        dispatch(beneficiaryChanged(customer.beneficiaryId));
      } else {
        // If "Customer" is selected, reset the beneficiary selection
        dispatch(beneficiaryChanged(''));
      }
    },
    [dispatch, customer.beneficiaryId],
  );

  const handleSameDayAchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(sameDayAchChanged(event.target.checked));
    },
    [dispatch],
  );

  const isAchTransfer = React.useMemo(
    (): boolean => currentDestination.rail === PaymentRailType.ACH,
    [currentDestination.rail],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.rejected) {
      setTimeout((): void => {
        mutation.reset();
      }, 1200);
    }
  }, [mutation, setRecipientAccount]);

  React.useEffect((): void => {
    if (recentlyAddedAccountId) {
      setRecipientAccount(recentlyAddedAccountId);
    }
  }, [recentlyAddedAccountId, setRecipientAccount]);

  React.useEffect((): void => {
    if (simulation.to.value !== null && simulation.from.value !== null) {
      dispatch(simulationCreated(simulation));
    }
  }, [dispatch, simulation]);

  // Load beneficiaries and set main beneficiary
  React.useEffect((): void => {
    if (fetchedBeneficiaries.length > 0 && beneficiaries.length === 0 && customer.beneficiaryId) {
      dispatch(beneficiariesLoaded(fetchedBeneficiaries));
      dispatch(mainBeneficiarySet(customer.beneficiaryId));

      // If there's only one beneficiary, auto-select it
      if (fetchedBeneficiaries.length === 1 && !selectedBeneficiaryId) {
        dispatch(beneficiaryChanged(customer.beneficiaryId));
      }
    }
  }, [
    fetchedBeneficiaries,
    beneficiaries.length,
    customer.beneficiaryId,
    selectedBeneficiaryId,
    dispatch,
  ]);

  const displayBeneficiaries = React.useMemo((): Beneficiary[] => {
    return beneficiaries.filter((b: Beneficiary): boolean => b.id !== mainBeneficiaryId);
  }, [beneficiaries, mainBeneficiaryId]);

  const customerFuse = React.useMemo((): Fuse<Beneficiary> => {
    return new Fuse(displayBeneficiaries, {
      keys: ['name'],
      threshold: 0.3,
      includeMatches: true,
    });
  }, [displayBeneficiaries]);

  const filteredCustomers = React.useMemo((): readonly FilteredBeneficiary[] => {
    if (customerSearchKeyword.trim() !== '') {
      const results = customerFuse.search(customerSearchKeyword);
      return results.map((result: FuseResult<Beneficiary>): FilteredBeneficiary => {
        const match = result.matches?.find((m: FuseResultMatch): boolean => m.key === 'name');
        return {
          item: result.item,
          matchIndices: match?.indices as ReadonlyArray<readonly [number, number]> | undefined,
        };
      });
    }

    return displayBeneficiaries.map((item: Beneficiary): FilteredBeneficiary => ({ item }));
  }, [customerFuse, customerSearchKeyword, displayBeneficiaries]);

  const onCustomerSearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: keyword } = event.target;
      setCustomerSearchKeyword(keyword);
    },
    [],
  );

  const fundsOverflowed = React.useMemo((): boolean => {
    if (fromAmount === null || fundingMethod.method !== PaymentRailType.CalizaWallet) {
      return false;
    }

    const effectiveBalances =
      fundingMethod?.fundingSource === 'customer' ? customerBalances : balances;
    const balance = effectiveBalances[fromCurrency];
    if (!balance) {
      return false;
    }

    return Math.round(100 * balance.available) < Math.round(100 * fromAmount);
  }, [
    balances,
    customerBalances,
    fromAmount,
    fromCurrency,
    fundingMethod.method,
    fundingMethod?.fundingSource,
  ]);

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr(
            'AMOUNT_BIGGER_THAN_BALANCE',
            formatter.format(currentBalance.available, currentBalance.currencyCode),
          ),
        )
        .otherwise((): null => null),
    [currentBalance.available, currentBalance.currencyCode, error, formatter, tr],
  );

  const currentTransactionDetails = React.useMemo((): NfTransactionDetailsResponse => {
    return simulation?.transactionDetails ?? emptyTransactionDetails;
  }, [simulation?.transactionDetails]);

  const shouldShowRecipientSection = React.useMemo(
    (): boolean =>
      beneficiaries.length <= 1 ||
      (senderType === 'myself' && !!selectedBeneficiaryId) ||
      (senderType === 'customer' && !!selectedBeneficiaryId),
    [beneficiaries.length, selectedBeneficiaryId, senderType],
  );

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title="MAKE_A_TRANSFER" onClose={onClose} />
      <FullScreenModalContainer>
        {beneficiaries.length > 1 && (
          <Section title={tr('SENDER_INFORMATION')} visible={true} working={creatingTransaction}>
            <FormControl fullWidth>
              <Typography variant="body1" mb={2}>
                {tr('WHO_ARE_YOU_SENDING_FUNDS_ON_BEHALF_OF')}
              </Typography>
              <RadioGroup value={senderType || ''} onChange={handleSenderTypeChange}>
                <StyledFormControlLabel value="myself" control={<Radio />} label={tr('MYSELF')} />
                <StyledFormControlLabel
                  value="customer"
                  control={<Radio />}
                  label={tr('CUSTOMER')}
                />
              </RadioGroup>
            </FormControl>
            {senderType === 'customer' && (
              <Box mt={3}>
                <FormControl fullWidth>
                  <InputLabel id="beneficiary-select-label">{tr('SELECT_CUSTOMER')}</InputLabel>
                  <Select
                    labelId="beneficiary-select-label"
                    id="beneficiary-select"
                    value={selectedBeneficiaryId}
                    label={tr('SELECT_CUSTOMER')}
                    onChange={handleBeneficiaryChange}
                    MenuProps={{
                      disablePortal: false,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}>
                    <SearchMenuItem onChange={onCustomerSearchKeywordChange} />
                    {filteredCustomers.map(
                      (filtered: FilteredBeneficiary): React.ReactElement => (
                        <MenuItem key={filtered.item.id} value={filtered.item.id}>
                          <HighlightedText
                            text={filtered.item.name}
                            indices={filtered.matchIndices}
                          />
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Section>
        )}
        <Section
          title={tr('RECIPIENT_INFORMATION')}
          visible={shouldShowRecipientSection}
          working={creatingTransaction}>
          <RecipientsSelect
            name="transferRecipient"
            loading={loadingContacts}
            accounts={accounts}
            value={recipientAccountId}
            onChange={setRecipientAccount}
            error={error === FormErrors.MISSING_ACCOUNT}
            helperText={error === FormErrors.MISSING_ACCOUNT ? tr('REQUIRED_RECIPIENT') : null}
          />
          <Box mt={4}>
            <AddObjectButton label={tr('ADD_NEW_RECIPIENT')} path="recipient" />
          </Box>
        </Section>
        <Section
          title={tr('FUNDING_METHOD')}
          visible={recipientAccountId !== ''}
          working={creatingTransaction}>
          <FundingMethodSelect
            label={tr('SELECT_FUNDING_CURRENCY')}
            balances={balances}
            items={fundingMethods}
            value={fundingMethod.id}
            onChange={setFundingMethod}
            error={error === FormErrors.MISSING_FUNDING_METHOD}
            helperText={
              error === FormErrors.MISSING_FUNDING_METHOD ? tr('REQUIRED_FUNDING_METHOD') : null
            }
            customerBalances={customerBalances}
            showCustomerSection={senderType === 'customer' && !!selectedBeneficiaryId}
            customerCurrencies={CUSTOMER_BALANCE_CURRENCIES}
          />
        </Section>
        <Section
          title={tr('TRANSACTION_DETAILS')}
          visible={fundingMethod.method !== PaymentRailType.unset}
          working={fetchingSimulation || creatingTransaction}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              name="sendAmount"
              size="normal"
              label={tr('YOU_SEND')}
              currency={fromCurrency}
              currencies={fromCurrencies}
              amount={fromAmount}
              autoFocus={true}
              error={fundsOverflowed || !!amountHelperText}
              helperText={amountHelperText}
              onAmountChange={setFromAmount}
            />
            <AmountAndCurrencyWidget
              name="receiveAmount"
              size="normal"
              label={tr('RECIPIENT_GETS')}
              currency={currentDestination.currency}
              currencies={toCurrencies}
              amount={toAmount}
              onAmountChange={setToAmount}
            />
            {isAchTransfer && (
              <Box sx={styles.sameDayAchContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sameDayAch}
                      onChange={handleSameDayAchChange}
                      disabled={creatingTransaction}
                    />
                  }
                  label={tr('SAME_DAY_ACH')}
                  sx={styles.sameDayAchLabel}
                />
              </Box>
            )}
            {fundsOverflowed && (
              <Box textAlign="center">
                <Typography color="error">{tr('YOU_DO_NOT_HAVE_ENOUGH_FUNDS')}</Typography>
              </Box>
            )}
            {shouldNotAllowOperation && <OutsideBRLMarketHoursAlert />}
            {showFxMarkup && (
              <FxMarkupBanner
                currentMarkupBps={appliedMarkupBps}
                disabled={fromAmount === null && toAmount === null}
                onAdd={(): void => setIsFxMarkupModalOpen(true)}
                onRemove={(): void => {
                  setAppliedMarkupBps(null);
                  dispatch(markupBpsChanged(null));
                }}
              />
            )}
            <StyledAccordion defaultExpanded={true}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="h7">{tr('TRANSACTION_DETAILS_TITLE')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TransactionDetails
                  transactionDetails={currentTransactionDetails}
                  fromAmount={fromAmount}
                  fromCurrency={fromCurrency}
                  toAmount={toAmount}
                  toCurrency={currentDestination.currency}
                  validityTimestamp={simulation.expiresAt}
                />
              </AccordionDetails>
            </StyledAccordion>
          </Box>

          <Box sx={styles.expectedSettlementTimeInfo}>
            <ExpectedSettlementTimeInfo
              expectedSettlementTimeDetails={
                simulation.transactionDetails.expectedSettlementTimeDetails
              }
            />
          </Box>
        </Section>
        {showFxMarkup && (
          <FxMarkupModal
            open={isFxMarkupModalOpen}
            onClose={(): void => {
              dispatch(markupBpsChanged(appliedMarkupBps));
              setIsFxMarkupModalOpen(false);
            }}
            onApply={(bps: number): void => {
              setAppliedMarkupBps(bps);
              dispatch(markupBpsChanged(bps));
              setIsFxMarkupModalOpen(false);
            }}
            onBpsChange={(bps: number | null): void => dispatch(markupBpsChanged(bps))}
            currentMarkupBps={appliedMarkupBps}
            simulation={simulation}
            fromCurrency={fromCurrency}
            toCurrency={currentDestination.currency}
            isLoading={fetchingSimulation}
          />
        )}
        <DocumentSections
          countryCode={destinationCountry}
          documentRequired={isDocumentRequired}
          creatingTransaction={creatingTransaction}
          simulation={simulation}
          attachment={attachment}
          transferPurpose={transferPurpose}
          paymentReference={paymentReference}
          error={
            error === FormErrors.MISSING_SUPPORTING_DOCUMENTATION
              ? tr('REQUIRED_SUPPORTING_DOCUMENTATION')
              : null
          }
          paymentReferenceError={
            error === FormErrors.PAYMENT_REFERENCE_INVALID_CHARACTERS
              ? tr('VALIDATION_INVALID_SWIFT_CHARACTERS', tr('RECIPIENT_MEMO'))
              : null
          }
          onPaymentReferenceChange={handlePaymentReferenceChange}
          onPurposeChange={handlePurposeChange}
          onFileChange={handleFileChange}
          onFileRemove={handleFileRemove}
        />
        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            disabled={confirmDisabled}
            loading={mutation.status === QueryStatus.pending}
            onClick={handleConfirm}>
            {tr('CONFIRM_PAYMENT')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>

      <Outlet context={reducer} />
    </ApplicationDialog>
  );
};

const emptyAmount: NotNullable<Amount> = {
  value: 0,
  currencyCode: CurrencyCodeType.UNSET,
};

const emptyTransactionDetails: NfTransactionDetailsResponse = {
  conversionDetails: {
    clientRate: 0,
    effectiveTransactionValue: 0,
    marketExchangeRate: 0,
    calizaRate: 0,
    amountToBeConverted: emptyAmount,
  },
  feeDetails: {
    totalFees: emptyAmount,
    integratorFee: emptyAmount,
    taxes: emptyAmount,
    fixedFees: [],
    oldFees: [],
    percentageFees: [],
    spreadFees: [],
    vet: '0.0000',
  },
  expectedSettlementTimeDetails: null,
};

const StyledFormControlLabel = styled(FormControlLabel)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    alignItems: 'center',
    color: theme.palette.text.primary,
  }),
);

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  sameDayAchContainer: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 4,
    px: 2,
    py: 1.5,
    width: '100%',
  },
  sameDayAchLabel: {
    m: 0,
    alignItems: 'center',
  },
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};

const getToRailFromAccount = (
  contacts: readonly Contact[],
  recipientAccountId: string,
): PaymentRailType => {
  const allRecipients =
    contacts
      .map((contact: Contact): readonly RecipientResponse[] => {
        return contact.recipients;
      }, [])
      ?.flat() ?? [];
  const selectedRecipient = allRecipients.find(
    (recipient: RecipientResponse): boolean => recipient.id === recipientAccountId,
  );
  if (!selectedRecipient) {
    return PaymentRailType.unset;
  }

  return selectedRecipient.type;
};

const getToCurrencyFromAccount = (
  contacts: readonly Contact[],
  recipientAccountId: string,
): CurrencyCodeType => {
  const allRecipients =
    contacts
      .map((contact: Contact): readonly RecipientResponse[] => {
        return contact.recipients;
      }, [])
      ?.flat() ?? [];
  const selectedRecipient = allRecipients.find(
    (recipient: RecipientResponse): boolean => recipient.id === recipientAccountId,
  );
  if (!selectedRecipient) {
    return CurrencyCodeType.UNSET;
  }

  return getRecipientCurrency(selectedRecipient);
};

const resetAmountRelatedErrors = (error: FormErrors | null): FormErrors | null => {
  if (error === FormErrors.MISSING_AMOUNT || error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE) {
    return null;
  }

  return error;
};

const getBankCountryFromRecipient = (recipient: RecipientResponse): CountryType | null => {
  if (recipient.type !== PaymentRailType.Swift) {
    return null;
  }

  const country = recipient.details.bankCountry as string | undefined;
  if (country && isCountryType(country)) {
    return country as CountryType;
  }

  const countryOfIncorporation = recipient.countryOfIncorporation;
  if (countryOfIncorporation && isCountryType(countryOfIncorporation)) {
    return countryOfIncorporation as CountryType;
  }

  console.debug('cannot determine the country for recipient', recipient);
  return null;
};
